import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, Sparkles, X } from "lucide-react";
import { useLang } from "@/i18n";
import { LEAGUE_PREFERENCES } from "@/constants/markets";

const FilterSelect = ({ value, onChange, allLabel, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      data-testid={testId}
      className="w-full bg-[hsl(var(--surface))] border-[hsl(var(--input))] focus:ring-2 focus:ring-[hsl(var(--news-red))] h-11"
    >
      <SelectValue placeholder={allLabel} />
    </SelectTrigger>
    <SelectContent className="bg-[hsl(var(--popover))]">
      <SelectItem value="all">{allLabel}</SelectItem>
      {options.map((opt) => (
        <SelectItem key={opt} value={opt}>
          {opt}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const FiltersBar = ({
  position,
  style,
  commitment,
  league,
  search,
  onPositionChange,
  onStyleChange,
  onCommitmentChange,
  onLeagueChange,
  onSearchChange,
  onClear,
  filtersActive,
  options,
}) => {
  const { t } = useLang();

  return (
    <div className="bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] lg:sticky lg:top-0 lg:z-20 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-2 mb-3">
          <SlidersHorizontal className="h-4 w-4 text-[hsl(var(--navy))]" />
          <span className="font-display uppercase tracking-[0.18em] text-xs text-[hsl(var(--navy))]">
            {t("scoutingDesk")}
          </span>
          {filtersActive && (
            <Button
              data-testid="clear-all-filters-button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="ms-auto h-7 text-xs text-[hsl(var(--news-red))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--news-red-dark))]"
            >
              <X className="h-3 w-3 me-1" /> {t("clearFilters")}
            </Button>
          )}
        </div>

        {/* Ask the Chief Scout search bar */}
        <div className="relative mb-3">
          <div className="absolute inset-y-0 start-3 flex items-center pointer-events-none">
            <Sparkles className="h-4 w-4 text-[hsl(var(--news-red))]" />
          </div>
          <Input
            data-testid="chief-scout-search-input"
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("searchPlaceholder")}
            aria-label={t("searchLabel")}
            className="w-full h-12 ps-10 pe-10 bg-[hsl(var(--surface))] border-[hsl(var(--input))] text-sm sm:text-base focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))] rounded-lg"
          />
          {search && (
            <button
              data-testid="clear-search-button"
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute inset-y-0 end-3 flex items-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--news-red))] transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterSelect
            value={position}
            onChange={onPositionChange}
            allLabel={t("allPositions")}
            options={options.positions}
            testId="position-filter-select"
          />
          <FilterSelect
            value={style}
            onChange={onStyleChange}
            allLabel={t("allStyles")}
            options={options.styles}
            testId="style-filter-select"
          />
          <FilterSelect
            value={league}
            onChange={onLeagueChange}
            allLabel={t("allLeagueTypes")}
            options={LEAGUE_PREFERENCES}
            testId="league-filter-select"
          />
          <FilterSelect
            value={commitment}
            onChange={onCommitmentChange}
            allLabel={t("allCommitments")}
            options={options.commitments}
            testId="commitment-filter-select"
          />
        </div>
      </div>
    </div>
  );
};
